import React, { useState, useRef } from "react";
import { Image, Modal, Input, Skeleton } from "antd";
import PopoverToolbar from "../EbulletinPopoverToolbar";
import UploadCard from "../EbulletinUploadCard";
import UploadModal from "../modals/EbulletinUploadModal";

export default function ImageBlock({
  block,
  isEditing,
  updateBlockProperty,
  updateBlockStyle,
  bulletinId,
  sectionId,
}) {
  const resizableRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showImagePopover, setShowImagePopover] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkInput, setLinkInput] = useState("");

  const getImageData = () => {
    let data;
    if (block.url) {
      data = {
        url:     block.url,
        alt:     block.alt     || "",
        caption: block.caption || "",
        style:   block.style   || {},
        link:    block.link    || "",
      };
    } else if (block.content) {
      try {
        const parsed = JSON.parse(block.content);
        data = parsed.url
          ? { link: "", ...parsed }
          : { url: block.content, alt: "", caption: "", style: {}, link: "" };
      } catch {
        data = { url: block.content, alt: "", caption: "", style: {}, link: "" };
      }
    } else {
      data = { url: "", alt: "", caption: "", style: {}, link: "" };
    }

    // Always prefer properties set directly on the block (e.g. by updateBlockProperty)
    if (block.link    !== undefined) data.link    = block.link;
    if (block.alt     !== undefined) data.alt     = block.alt;
    if (block.caption !== undefined) data.caption = block.caption;
    if (block.style   !== undefined) data.style   = block.style;

    return data;
  };

  const imageData = getImageData();
  const imageUrl  = imageData.url;

  const initResize = (e, direction) => {
    const img = resizableRef.current;
    if (!img) return;
    e.preventDefault();
    const startX     = e.clientX;
    const startWidth = img.offsetWidth;
    const doDrag = (moveEvent) => {
      const dx       = moveEvent.clientX - startX;
      const newWidth = direction === "left" ? startWidth - dx : startWidth + dx;
      img.style.width = `${Math.max(100, newWidth)}px`;
    };
    const stopDrag = () => {
      document.removeEventListener("mousemove", doDrag);
      document.removeEventListener("mouseup", stopDrag);
      if (img) {
        updateBlockStyle({ ...imageData.style, width: img.style.width });
      }
    };
    document.addEventListener("mousemove", doDrag);
    document.addEventListener("mouseup", stopDrag);
  };

  const handleRemoveImage = async () => {
    const urlToDelete = imageUrl;
    // Clear the image from the block immediately for instant UI feedback
    updateBlockProperty("url", "");
    updateBlockProperty("content", "");
    // Tell the backend to delete the file
    if (urlToDelete) {
      try {
        const token = localStorage.getItem("api_token");
        await fetch(`${process.env.REACT_APP_API_URL}/ebulletin/upload`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ url: urlToDelete }),
        });
      } catch (err) {
        console.error("Failed to delete file from server:", err);
      }
    }
  };

  const handleUploadFinish = (uploadResult) => {
    try {
      let newImageUrl, fileName, fileSize, mimeType;
      if (typeof uploadResult === "string") {
        newImageUrl = uploadResult;
      } else {
        newImageUrl = uploadResult.dataUrl || uploadResult.url;
        fileName    = uploadResult.fileName || uploadResult.name;
        fileSize    = uploadResult.fileSize || uploadResult.size;
        mimeType    = uploadResult.mimeType || uploadResult.type;
      }
      updateBlockProperty("url",     newImageUrl);
      updateBlockProperty("alt",     imageData.alt);
      updateBlockProperty("caption", imageData.caption);
      if (fileName) updateBlockProperty("fileName", fileName);
      if (fileSize) updateBlockProperty("fileSize", fileSize);
      if (mimeType) updateBlockProperty("mimeType", mimeType);
      setShowUploadModal(false);
    } catch (error) {
      console.error("Image upload error:", error);
    }
  };

  const handleAlignChange   = (align)   => updateBlockStyle({ ...imageData.style, textAlign: align });
  const handleAltTextChange = (alt)     => updateBlockProperty("alt", alt);
  const handleCaptionChange = (caption) => updateBlockProperty("caption", caption);

  const openLinkModal = () => {
    setLinkInput(imageData.link || "");
    setShowImagePopover(false);
    setShowLinkModal(true);
  };

  const saveLinkModal = () => {
    updateBlockProperty("link", linkInput.trim());
    setShowLinkModal(false);
  };

  const removeLinkModal = () => {
    updateBlockProperty("link", "");
    setLinkInput("");
    setShowLinkModal(false);
  };

  // The raw <img> used in edit mode (caption is shown via Input below, not here)
  const editableImage = (
    <div className="image-wrapper">
      <img
        ref={resizableRef}
        src={imageUrl}
        alt={imageData.alt || "uploaded content"}
        className="resizable-image"
        draggable={false}
        style={imageData.style || {}}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    </div>
  );

  // View-mode rendering: link wraps the image; no link uses Ant Design's built-in zoom
  const viewImage = imageData.link ? (
    <div className="image-wrapper">
      <a href={imageData.link} target="_blank" rel="noopener noreferrer">
        <img
          src={imageUrl}
          alt={imageData.alt || ""}
          className="resizable-image"
          style={{ ...imageData.style, cursor: "pointer" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </a>
    </div>
  ) : (
    <div className="image-wrapper">
      <Image
        src={imageUrl}
        alt={imageData.alt || ""}
        className="resizable-image"
        style={imageData.style || {}}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    </div>
  );

  return (
    <div className="block-card image" style={{ textAlign: imageData.style?.textAlign || "left" }}>
      <UploadModal
        type="image"
        visible={showUploadModal}
        setVisible={setShowUploadModal}
        onUploadFinish={handleUploadFinish}
        bulletinId={bulletinId}
        sectionId={sectionId}
      />

      {/* Link input modal */}
      <Modal
        title="Image Link"
        open={showLinkModal}
        onOk={saveLinkModal}
        onCancel={() => setShowLinkModal(false)}
        okText="Save"
        footer={[
          imageData.link && (
            <span
              key="remove"
              onClick={removeLinkModal}
              style={{ float: "left", color: "#ff4d4f", cursor: "pointer", lineHeight: "32px" }}
            >
              Remove link
            </span>
          ),
          <button key="cancel" onClick={() => setShowLinkModal(false)} className="ant-btn">
            Cancel
          </button>,
          <button key="ok" onClick={saveLinkModal} className="ant-btn ant-btn-primary">
            Save
          </button>,
        ]}
      >
        <Input
          prefix={<span style={{ color: "#888" }}>URL</span>}
          placeholder="https://example.com"
          value={linkInput}
          onChange={(e) => setLinkInput(e.target.value)}
          onPressEnter={saveLinkModal}
          autoFocus
        />
        <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
          Clicking the image will open this link in a new tab.
          Leave empty to enable zoom on click instead.
        </div>
      </Modal>

      {!imageUrl ? (
        isEditing ? (
          <UploadCard type="image" onClick={() => setShowUploadModal(true)} />
        ) : null
      ) : (
        <>
          <div className="resizable-container">
            {isEditing ? (
              <>
                <div className="resize-handle left"  onMouseDown={(e) => initResize(e, "left")} />
                <PopoverToolbar
                  showPopover={showImagePopover}
                  onOpenChange={setShowImagePopover}
                  trigger="click"
                  showUploadControls={true}
                  showAlignControls={true}
                  showImageLinkControl={true}
                  showRemoveControl={true}
                  currentAlign={imageData.style?.textAlign || "left"}
                  currentLink={imageData.link || ""}
                  onUploadClick={() => { setShowUploadModal(true); setShowImagePopover(false); }}
                  onAlignChange={handleAlignChange}
                  onAltTextChange={handleAltTextChange}
                  onCaptionChange={handleCaptionChange}
                  onLinkClick={openLinkModal}
                  onRemoveImage={handleRemoveImage}
                >
                  {editableImage}
                </PopoverToolbar>
                <div className="resize-handle right" onMouseDown={(e) => initResize(e, "right")} />
              </>
            ) : (
              viewImage
            )}
          </div>

          {/* Optional caption — editable input in edit mode, plain text in view mode */}
          {isEditing ? (
            <input
              className="image-caption-input"
              placeholder="Add a caption... (optional)"
              value={imageData.caption || ""}
              onChange={(e) => handleCaptionChange(e.target.value)}
            />
          ) : (
            imageData.caption && (
              <p className="image-caption">{imageData.caption}</p>
            )
          )}
        </>
      )}
    </div>
  );
}
